import moment from "moment";
import { PAGE_TYPE } from "../types/pages.type";
import { setFilterDateUpdate } from "../store/reducers/common.slice";
import { setIsComparisonRangeSet } from "../store/reducers/comparisonReducers/comparison.slice";
import { setFilterDateRange as setAdmissionDateFilter } from "../store/reducers/admission.slice";
import { setFilterDateRange as setCommunityDateFilter } from "../store/reducers/community-transfer.slice";
import { setFilterDateRange as setDescentDateFilter } from "../store/reducers/deceased.slice";
import { setFilterDateRange as setHospitalDateFilter } from "../store/reducers/hospital.slice";
import { setFilterDateRange as setOverallDateFilter } from "../store/reducers/overall.slice";
import { setFilterDateRange as setQuickGlaceDateFilter } from "../store/reducers/quick-glace.slice";
import { primeManualDateSelectionForImportedRange } from "../components/dashboard/filter/DateFilterComponent";

/** Session flag consumed once on Admission mount to run the same refresh path as choosing dates in DateFilterComponent. */
export const PAGE_CONVERTER_ADMISSION_FETCH_FLAG = "adt_pageConverterAdmissionFetch_v1";

/**
 * Which dashboard the user converted from when navigating to Admissions (set with the fetch flag on "Go").
 * Consumed once on Admission mount; pair with per-dashboard card context keys below.
 */
export const PAGE_CONVERTER_ADMISSION_SOURCE_PAGE_KEY = "adt_pageConverterAdmissionSourcePage_v1";

export function persistPageConverterAdmissionSource(pageType) {
	try {
		sessionStorage.setItem(PAGE_CONVERTER_ADMISSION_SOURCE_PAGE_KEY, JSON.stringify({ pageType }));
	} catch {
		/* quota / private mode */
	}
}

/** @returns {{ pageType: string } | null} */
export function readAndClearPageConverterAdmissionSource() {
	try {
		const raw = sessionStorage.getItem(PAGE_CONVERTER_ADMISSION_SOURCE_PAGE_KEY);
		if (raw) {
			sessionStorage.removeItem(PAGE_CONVERTER_ADMISSION_SOURCE_PAGE_KEY);
			return JSON.parse(raw);
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Snapshot of Hospital dashboard card filters when filter list totals are recalculated (for Admission / transfer-status flows).
 * Written from Hospital.updateTotalToDBFilter; read on Admission when needed.
 */
export const HOSPITAL_PAGE_CONVERTER_CARD_CONTEXT_KEY = "adt_hospitalPageConverterCardContext_v1";

export function persistHospitalPageConverterCardContext(snapshot) {
	try {
		sessionStorage.setItem(HOSPITAL_PAGE_CONVERTER_CARD_CONTEXT_KEY, JSON.stringify(snapshot));
	} catch {
		/* quota / private mode */
	}
}

export function readHospitalPageConverterCardContext() {
	try {
		const raw = sessionStorage.getItem(HOSPITAL_PAGE_CONVERTER_CARD_CONTEXT_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

export function clearHospitalPageConverterCardContext() {
	try {
		sessionStorage.removeItem(HOSPITAL_PAGE_CONVERTER_CARD_CONTEXT_KEY);
	} catch {
		/* ignore */
	}
}

export const COMMUNITY_PAGE_CONVERTER_CARD_CONTEXT_KEY = "adt_communityPageConverterCardContext_v1";

export function persistCommunityPageConverterCardContext(snapshot) {
	try {
		sessionStorage.setItem(COMMUNITY_PAGE_CONVERTER_CARD_CONTEXT_KEY, JSON.stringify(snapshot));
	} catch {
		/* quota / private mode */
	}
}

export function readCommunityPageConverterCardContext() {
	try {
		const raw = sessionStorage.getItem(COMMUNITY_PAGE_CONVERTER_CARD_CONTEXT_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

export function clearCommunityPageConverterCardContext() {
	try {
		sessionStorage.removeItem(COMMUNITY_PAGE_CONVERTER_CARD_CONTEXT_KEY);
	} catch {
		/* ignore */
	}
}

export const DECEASED_PAGE_CONVERTER_CARD_CONTEXT_KEY = "adt_deceasedPageConverterCardContext_v1";

export function persistDeceasedPageConverterCardContext(snapshot) {
	try {
		sessionStorage.setItem(DECEASED_PAGE_CONVERTER_CARD_CONTEXT_KEY, JSON.stringify(snapshot));
	} catch {
		/* quota / private mode */
	}
}

export function readDeceasedPageConverterCardContext() {
	try {
		const raw = sessionStorage.getItem(DECEASED_PAGE_CONVERTER_CARD_CONTEXT_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

export function clearDeceasedPageConverterCardContext() {
	try {
		sessionStorage.removeItem(DECEASED_PAGE_CONVERTER_CARD_CONTEXT_KEY);
	} catch {
		/* ignore */
	}
}

/** Read card-filter snapshot for a converter source page (does not clear session). */
export function readPageConverterCardContextForPageType(pageType) {
	switch (pageType) {
		case PAGE_TYPE.HOSPITAL:
			return readHospitalPageConverterCardContext();
		case PAGE_TYPE.COMMUNITY_TRANSFER:
			return readCommunityPageConverterCardContext();
		case PAGE_TYPE.DECEASED:
			return readDeceasedPageConverterCardContext();
		default:
			return null;
	}
}

function getRowLastAdmissionRaw(p) {
	if (!p) return null;
	if (p.lastAdmissionDate != null && String(p.lastAdmissionDate).trim() !== "") {
		return p.lastAdmissionDate;
	}
	const dola = p.dateOfLatestAdmission;
	if (dola == null) return null;
	if (typeof dola === "object" && dola.dateOfADT != null) return dola.dateOfADT;
	return dola;
}

function coerceAdmissionDateMoment(value) {
	if (value == null) return null;
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		// Local calendar day (avoid UTC startOf, which shifts date for US timezones).
		return moment(value).startOf("day");
	}
	const s = String(value).trim();
	if (!s) return null;
	const strict = moment(s, "YYYY-MM-DD", true);
	// Date-only fields (lastAdmissionDate) are calendar dates — parse as local, not UTC midnight.
	if (strict.isValid()) return strict.clone().startOf("day");
	const loose = moment(s);
	return loose.isValid() ? loose.clone().startOf("day") : null;
}

/**
 * Builds a dashboard date range from list rows (lastAdmissionDate), matching DateFilterComponent shape.
 * @returns {{ key: string, startDate: Date, endDate: Date } | null}
 */
export function buildDashboardDateRangeFromPatientList(patientList) {
	const moments = (patientList || [])
		.map((p) => coerceAdmissionDateMoment(getRowLastAdmissionRaw(p)))
		.filter((m) => m && m.isValid());
	if (!moments.length) {
		return null;
	}
	const sorted = [...moments].sort((a, b) => a.valueOf() - b.valueOf());
	return {
		key: "selection",
		startDate: sorted[0].clone().startOf("day").toDate(),
		endDate: sorted[sorted.length - 1].clone().startOf("day").toDate(),
	};
}

/** @deprecated Use buildDashboardDateRangeFromPatientList */
export const buildDashboardDateRangeFromHospitalPatientList = buildDashboardDateRangeFromPatientList;

/**
 * Applies the same cross-dashboard filter updates as DateFilterComponent.updateDateToAllDashboard,
 * plus flags aligned with a successful handleOnChange path. Does not open comparison popups.
 */
export function applyHospitalToAdmissionPageConverter(dispatch, updateDashboard, latestValue) {
	if (!latestValue?.startDate || !latestValue?.endDate) {
		return;
	}
	primeManualDateSelectionForImportedRange(latestValue);
	dispatch(setIsComparisonRangeSet(true));
	dispatch(setFilterDateUpdate(false));
	dispatch(setHospitalDateFilter(latestValue));
	dispatch(setCommunityDateFilter(latestValue));
	dispatch(setAdmissionDateFilter(latestValue));
	dispatch(setDescentDateFilter(latestValue));
	dispatch(setOverallDateFilter(latestValue));
	dispatch(setQuickGlaceDateFilter(latestValue));
	if (typeof updateDashboard === "function") {
		updateDashboard({ filter: latestValue });
	}
}
